/**
 * 隠れスキル診断ポップアップのロジック（UI は aptitude/SkillDiagnosisPopup）。
 */
import axios from 'axios'
import localforage from 'localforage'
import { computed, nextTick, ref, watch, type Ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { emitter } from '@/plugins/emitter'
import {
  useSkillDiagnosisPopupStore,
  type SkillDiagnosisOpenOptions
} from '@/stores/skillDiagnosisPopup'

export type SkillDiagnosisHost = {
  $varuna: {
    endPoints: {
      runSkillDiagnosis: string
      runSkillDiagnosisLite: string
    }
  }
  $globalVar: {
    isYagiPrime: boolean
    isYagiPrimeCancel: boolean
  }
  $cookies: {
    set: (name: string, value: unknown, expire?: number | string) => void
  }
}

type DiagnosisResult = {
  success?: boolean
  message?: string
  zone?: string
  strength_type?: string
  job_category_1?: string
  job_category_2?: string
  job_category_3?: string
  diagnosis_comment?: string
}

type ResumeLike = {
  birthYear?: string | number
  birthMonth?: string | number
  birthDay?: string | number
  lastestEducational?: string
  jobHistories?: Array<{ description?: string }>
  educationalHistories?: Array<{ description?: string }>
}

const ZONE_LABELS: Record<string, string> = {
  A: 'キャリアアップ型',
  B: 'ハイブリッド型',
  C: '即戦力型'
}

export function useSkillDiagnosisPopup(host: Ref<SkillDiagnosisHost | null>) {
  const store = useSkillDiagnosisPopupStore()
  const { user } = useAuth()

  const state = ref<'ready' | 'loading' | 'result' | 'error'>('ready')
  const canDiagnose = ref(true)
  const insufficientMessage = ref('')
  const result = ref<DiagnosisResult | null>(null)
  const errorMessage = ref('')
  const resumeData = ref<ResumeLike | null>(null)
  const hasResumeData = ref(false)
  const needsJobHistory = ref(false)
  const hidePopupFor10Days = ref(false)

  const isYagiPrime = computed(() => {
    const g = host.value?.$globalVar
    return !!(g && g.isYagiPrime && !g.isYagiPrimeCancel)
  })

  const zoneDisplayName = computed(() => {
    if (!result.value?.zone) return ''
    return ZONE_LABELS[result.value.zone] || result.value.zone
  })

  const showSkipOption = computed(() => store.showSkipOption)

  function getUuid() {
    return user.value?.uid || null
  }

  async function checkData() {
    const uuid = getUuid()
    if (!uuid) {
      canDiagnose.value = false
      insufficientMessage.value = 'ログインが必要です。'
      return
    }

    let basicData: ResumeLike | null = null
    let formatData: ResumeLike | null = null

    await localforage.iterate((value, key) => {
      if (key === 'basicData') {
        basicData = value as ResumeLike
      }
      if (
        typeof key === 'string' &&
        key.indexOf('format_') === 0 &&
        value &&
        !formatData
      ) {
        formatData = value as ResumeLike
      }
    })

    resumeData.value = Object.assign({}, basicData || {}, formatData || {})
    const fd = resumeData.value
    const hasDate = !!(fd.birthYear && fd.birthMonth && fd.birthDay)
    const hasJobs = !!(fd.jobHistories && fd.jobHistories.length > 0)

    if (!basicData && !formatData) {
      canDiagnose.value = false
      hasResumeData.value = false
      insufficientMessage.value =
        '履歴書データが見つかりません。先に履歴書を作成してください。'
    } else if (!hasDate && !hasJobs) {
      canDiagnose.value = false
      hasResumeData.value = true
      needsJobHistory.value = true
      insufficientMessage.value =
        '履歴書の生年月日と職歴を入力すると診断できます。'
    } else if (!hasDate) {
      canDiagnose.value = false
      hasResumeData.value = true
      insufficientMessage.value = '履歴書の生年月日を入力すると診断できます。'
    } else if (!hasJobs) {
      canDiagnose.value = false
      hasResumeData.value = true
      needsJobHistory.value = true
      insufficientMessage.value = '履歴書の職歴を入力すると診断できます。'
    } else {
      canDiagnose.value = true
      hasResumeData.value = true
      insufficientMessage.value = ''
    }
  }

  async function onOpen(_options?: SkillDiagnosisOpenOptions | null) {
    state.value = 'ready'
    result.value = null
    errorMessage.value = ''
    hidePopupFor10Days.value = false
    needsJobHistory.value = false
    await checkData()
  }

  watch(
    () => store.visible,
    (v) => {
      if (v) {
        onOpen()
      }
    }
  )

  function closePopup() {
    const h = host.value
    if (hidePopupFor10Days.value && store.showSkipOption && h) {
      const tenDaysLater = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)
      h.$cookies.set(
        '_YAGISH_AGENTFORM_POPUP_HIDE_UNTIL',
        tenDaysLater.getTime(),
        60 * 60 * 24 * 10
      )
      hidePopupFor10Days.value = false
    }
    store.close()
  }

  function setVisible(value: boolean) {
    if (!value) closePopup()
    else store.setVisible(true)
  }

  async function runDiagnosis() {
    const h = host.value
    const uuid = getUuid()
    const fd = resumeData.value
    if (!uuid || !fd || !h) {
      state.value = 'error'
      errorMessage.value =
        'データが不足しています。ポップアップを開き直してください。'
      return
    }

    state.value = 'loading'

    try {
      const jobDescriptions = (fd.jobHistories || [])
        .map((j) => j.description || '')
        .filter((d) => d)

      const eduHistories = fd.educationalHistories || []
      const latestEdu =
        eduHistories.length > 0
          ? eduHistories[eduHistories.length - 1].description || ''
          : ''

      const payload = {
        uuid,
        birthyear: fd.birthYear,
        birthmonth: fd.birthMonth,
        birthday: fd.birthDay,
        lastest_educational: fd.lastestEducational || '',
        educational_text: latestEdu,
        job_histories: jobDescriptions
      }

      const endpoint = isYagiPrime.value
        ? h.$varuna.endPoints.runSkillDiagnosis
        : h.$varuna.endPoints.runSkillDiagnosisLite

      const response = await axios.post(endpoint, JSON.stringify(payload), {
        timeout: 60000,
        headers: { 'Content-Type': 'application/json' }
      })

      if (response.data && response.data.success) {
        result.value = response.data
        state.value = 'result'
      } else {
        errorMessage.value =
          (response.data && response.data.message) ||
          '診断に失敗しました。時間をおいて再度お試しください。'
        state.value = 'error'
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } }
      }
      errorMessage.value =
        (err.response && err.response.data && err.response.data.message) ||
        '診断に失敗しました。時間をおいて再度お試しください。'
      state.value = 'error'
    }
  }

  function resetToReady() {
    state.value = 'ready'
    result.value = null
    errorMessage.value = ''
  }

  function openPaymentDialog() {
    const h = host.value
    closePopup()

    const onResult = (resultPayload: unknown) => {
      const ok =
        resultPayload === 'success' ||
        (resultPayload &&
          typeof resultPayload === 'object' &&
          (resultPayload as { status?: string }).status === 'success')
      if (ok && h) {
        emitter.emit('hide-yagiprime-payment-dialog')
        h.$globalVar.isYagiPrime = true
        h.$globalVar.isYagiPrimeCancel = false
        nextTick(() => {
          store.open()
          runDiagnosis()
        })
      }
      emitter.off('payment_yagiprime_result', onResult)
    }
    emitter.on('payment_yagiprime_result', onResult)
    emitter.emit('show-yagiprime-payment-dialog', 'skill-diagnosis')
  }

  return {
    store,
    state,
    canDiagnose,
    insufficientMessage,
    result,
    errorMessage,
    hasResumeData,
    needsJobHistory,
    hidePopupFor10Days,
    showSkipOption,
    isYagiPrime,
    zoneDisplayName,
    closePopup,
    setVisible,
    runDiagnosis,
    resetToReady,
    openPaymentDialog
  }
}
